import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../../../components/SEOHead';
import HormuzHubNav from '../../../components/HormuzHubNav';
import StitchHeroImage from '../../../components/StitchHeroImage';
import { STITCH_IMAGES } from '../../../constants/stitchAssets';
import { chokepointsAPI } from '../../../services/api';
import {
  getOrganizationSchema,
  getWebSiteSchema,
  getBreadcrumbSchema,
  getDatasetSchema,
  getArticleSchema,
  getFAQSchema,
} from '../../../utils/seoSchemas';
import {
  Activity,
  ArrowRight,
  Map,
  PieChart,
  Globe2,
  Droplet,
  TrendingUp,
  ExternalLink,
} from 'lucide-react';
import RiskIndicator from '../../../components/RiskIndicator';

const HormuzOverview = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await chokepointsAPI.getOverview('hormuz');
        setData(res.data);
      } catch (e) {
        console.error('Failed to load Hormuz overview', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center px-4">
        <div className="h-12 w-12 animate-spin rounded-full border-2 border-primary-container border-t-transparent dark:border-primary" />
      </div>
    );
  }

  const article = data?.article;
  const risk = data?.riskScore;
  const latestNews = data?.latestNews || [];
  const faqs = data?.faqs || [];

  const title = 'Complete Guide to the Strait of Hormuz (2026 Update)';
  const metaTitle = 'Strait of Hormuz Oil & Trade Risk Guide (2026) | TradeRadar';
  const description =
    "Data-driven guide to oil flows, shipping routes, risk scores, and the economic impact of disruptions in the Strait of Hormuz, one of the world's most important energy chokepoints.";

  // JSON-LD Schemas
  const breadcrumbJsonLd = getBreadcrumbSchema([
    { name: 'Home', path: '/' },
    { name: 'Strait of Hormuz Authority Hub', path: '/strait-of-hormuz' },
  ]);

  const organizationJsonLd = getOrganizationSchema();
  const websiteJsonLd = getWebSiteSchema();

  const faqJsonLd = faqs.length > 0 ? getFAQSchema(faqs) : null;

  const articleJsonLd = article
    ? getArticleSchema({
        ...article,
        path: '/strait-of-hormuz',
        seo_title: article.seo_title || title,
        seo_description: article.seo_description || description,
      })
    : null;

  // Dataset schema for statistics
  const datasetJsonLd = getDatasetSchema(
    'Strait of Hormuz Statistics',
    'Key metrics and data on oil flows, shipping volumes, and global trade impact',
    {
      path: '/strait-of-hormuz',
      keywords: ['oil flow', 'shipping', 'trade', 'chokepoint', 'statistics'],
      datePublished: article?.created_at || '2026-03-02T00:00:00Z',
      dateModified: article?.updated_at || '2026-03-02T00:00:00Z',
      variableMeasured: [
        'Daily Oil Flow (barrels/day)',
        'Global Oil Percentage (%)',
        'Seaborne Trade Share (%)',
        'Narrowest Width (nautical miles)',
      ],
    }
  );

  const jsonLd = [
    organizationJsonLd,
    websiteJsonLd,
    breadcrumbJsonLd,
    ...(articleJsonLd ? [articleJsonLd] : []),
    ...(faqJsonLd ? [faqJsonLd] : []),
    datasetJsonLd,
  ];

  const compositeScore = risk?.scores?.composite != null ? Number(risk.scores.composite) : null;
  const riskLabel = risk?.risk_level ? String(risk.risk_level).toUpperCase() : '—';

  return (
    <article className="py-8">
      <SEOHead
        title={metaTitle}
        description={description}
        path="/strait-of-hormuz"
        type="article"
        keywords="Strait of Hormuz, oil chokepoint, Hormuz risk today, global oil flow, shipping routes, Hormuz economic impact, Persian Gulf oil"
        jsonLd={jsonLd}
        image="/api/og-image/hormuz-overview.png"
        imageAlt="Strait of Hormuz Overview - Oil flows, risk scores, and shipping routes"
        author="TradeRadar"
        publishedTime={article?.created_at}
        modifiedTime={article?.updated_at}
      />

      <div className="relative left-1/2 mb-10 w-screen max-w-[100vw] -translate-x-1/2">
        <StitchHeroImage
          src={STITCH_IMAGES.hormuzHero.src}
          alt={STITCH_IMAGES.hormuzHero.alt}
          minHeightClass="min-h-[520px] md:min-h-[560px]"
        >
          <div className="max-w-7xl mx-auto w-full px-4 pb-2 sm:px-6 lg:px-8">
            <span className="mb-4 inline-flex items-center rounded-full border border-white/25 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white backdrop-blur-md dark:border-primary/40 dark:bg-primary/20 dark:text-on-primary-container">
              Authority hub · regularly updated
            </span>
            <h1 className="max-w-4xl font-headline text-3xl font-bold tracking-tight text-white drop-shadow-sm dark:text-on-surface md:text-4xl lg:text-5xl">
              {title}
            </h1>
            <p className="mt-3 max-w-2xl text-sm text-white/90 dark:text-on-surface-variant md:text-base">
              Statistics, routes, scenarios, and risk context for the world&apos;s most watched energy chokepoint.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-3 md:grid-cols-4">
              <div className="rounded-2xl border border-white/20 bg-white/10 px-4 py-3 backdrop-blur-xl dark:border-white/10 dark:bg-black/25">
                <p className="text-[10px] font-bold uppercase tracking-wide text-white/80 dark:text-on-surface-variant">
                  Composite risk
                </p>
                <p className="mt-1 font-headline text-2xl font-bold text-white dark:text-on-surface">
                  {compositeScore != null && !Number.isNaN(compositeScore) ? `${Math.round(compositeScore)}` : '—'}
                </p>
              </div>
              <div className="rounded-2xl border border-white/20 bg-white/10 px-4 py-3 backdrop-blur-xl dark:border-white/10 dark:bg-black/25">
                <p className="text-[10px] font-bold uppercase tracking-wide text-white/80 dark:text-on-surface-variant">
                  Risk band
                </p>
                <p className="mt-1 font-headline text-lg font-bold text-white dark:text-on-surface">{riskLabel}</p>
              </div>
              <div className="rounded-2xl border border-white/20 bg-white/10 px-4 py-3 backdrop-blur-xl dark:border-white/10 dark:bg-black/25">
                <p className="text-[10px] font-bold uppercase tracking-wide text-white/80 dark:text-on-surface-variant">
                  Global oil share
                </p>
                <p className="mt-1 font-headline text-2xl font-bold text-white dark:text-on-surface">~20%</p>
              </div>
              <div className="rounded-2xl border border-white/20 bg-white/10 px-4 py-3 backdrop-blur-xl dark:border-white/10 dark:bg-black/25">
                <p className="text-[10px] font-bold uppercase tracking-wide text-white/80 dark:text-on-surface-variant">
                  Brief updated
                </p>
                <p className="mt-1 text-sm font-semibold text-white dark:text-on-surface">
                  {article?.updated_at
                    ? new Date(article.updated_at).toLocaleDateString()
                    : 'March 2026'}
                </p>
              </div>
            </div>
          </div>
        </StitchHeroImage>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <HormuzHubNav />

      <p className="mb-6 text-sm text-slate-600 dark:text-on-surface-variant">
        <Link
          to="/insights/hormuz-trade-shocks"
          className="inline-flex items-center gap-1 font-medium text-primary-600 hover:underline dark:text-primary"
        >
          Research note: how Hormuz disruptions propagate to freight and prices
          <ArrowRight className="h-4 w-4" />
        </Link>
      </p>

      {/* A. Overview (intro below hero) */}
      <header className="mb-8">
        <p className="label-intel mb-2 text-slate-500 dark:text-outline">Strait of Hormuz authority hub</p>
        <p className="text-xs text-slate-600 dark:text-on-surface-variant mb-1">
          Author: <span className="font-medium">TraderRadar Analytics Team</span>
        </p>
        <p className="text-xs text-slate-600 dark:text-on-surface-variant mb-4">
          Last updated:{' '}
          {article?.updated_at
            ? new Date(article.updated_at).toLocaleDateString()
            : 'March 2026'}{' '}
          · Indicative, data‑informed analysis only – not investment or trading advice.
        </p>
        <p className="text-sm md:text-base text-slate-600 dark:text-on-surface-variant leading-relaxed">
          The Strait of Hormuz is the narrow channel that connects Gulf producers to global buyers.
          A large share of the world&apos;s crude oil and liquefied natural gas exports moves
          through this chokepoint every day. This hub brings together statistics, shipping routes,
          country exposure, historical incidents, and a risk score so that traders, policy
          teams, and logistics planners can see Hormuz as a full risk system rather than a single
          headline.
        </p>
      </header>

      {/* B. Why It Matters (featured snippet + stats) */}
      <section className="mb-10">
        {/* SEO Featured Snippet Block */}
        <div className="mb-6 p-4 rounded-lg bg-primary-50 dark:bg-primary-900/20 border border-primary-100 dark:border-primary-800">
          <p className="text-sm text-slate-700 dark:text-on-surface-variant">
            <strong>Why is the Strait of Hormuz important?</strong> The Strait of Hormuz is one of
            the world&apos;s most critical maritime chokepoints, carrying an estimated{' '}
            <strong>tens of millions of barrels of oil per day</strong>—roughly{' '}
            <strong>around one‑fifth of global oil consumption</strong> and a large share of
            seaborne‑traded crude. Sustained disruption can quickly raise freight costs, insurance
            premiums, and benchmark oil prices, with knock‑on effects for inflation, fiscal
            balances, and trade flows worldwide.
          </p>
        </div>

        {/* Key Statistics Grid */}
        <div className="grid md:grid-cols-3 gap-6">
          <div className="col-span-2">
            <h2 className="text-xl font-semibold text-slate-900 dark:text-on-surface mb-4">
              Key Statistics Snapshot
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <StatCard
                label="Approx. share of global oil flows"
                value="≈ 20–22%"
                icon={PieChart}
              />
              <StatCard
                label="Daily oil & products volume"
                value="≈ 17–21M bbl/day"
                icon={Droplet}
              />
              <StatCard
                label="Share of seaborne oil trade"
                value="≈ 30%"
                icon={Globe2}
              />
              <StatCard
                label="Narrowest navigable width"
                value="≈ 21 nm"
                icon={Map}
              />
            </div>
          </div>

          {/* F. Reusable Risk Indicator */}
          {risk && (
            <div className="flex flex-col space-y-4">
              <RiskIndicator
                level={risk.risk_level?.toUpperCase?.() || 'MEDIUM'}
                score={Number(risk.scores?.composite) || 50}
                lastUpdated={risk.calculated_at}
                timeHorizon="Next 30 days"
                headline="Current conditions reflect elevated but manageable risk, with markets watching naval deployments, insurance premia, and freight spreads for signs of stress."
                drivers={[
                  'Composite score blends indicators from reported incidents, oil price volatility, freight and insurance markets, and news sentiment.',
                  'Short‑term spikes in military tension can move the score quickly even if physical flows remain steady.',
                  'Sustained pressure on insurance and freight costs tends to keep the score elevated even after headline risk fades.',
                ]}
              />
              <Link
                to="/strait-of-hormuz/risk-dashboard"
                className="inline-flex items-center text-sm text-primary-600 dark:text-primary hover:underline"
              >
                View full risk dashboard
                <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* C. Economic Impact Overview */}
      <section className="mb-10 rounded-stitch-xl border border-slate-200/80 bg-white dark:border-outline-variant/15 dark:bg-surface-container p-6">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-on-surface mb-3">
          Economic Impact at a Glance
        </h2>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant mb-3">
          Because so much oil passes through a single corridor, disruptions can influence global
          benchmarks, freight costs, and economic growth. The scale of impact depends on how severe
          and how long any disruption lasts.
        </p>
        <ul className="space-y-2 text-sm text-slate-700 dark:text-on-surface-variant list-disc list-inside">
          <li>
            <strong>Partial disruptions</strong> – where flows are reduced but not halted – have
            historically been associated with price moves in the order of tens of percent, depending
            on broader market tightness.
          </li>
          <li>
            <strong>More extreme scenarios</strong> – such as prolonged or near‑complete blockages –
            could push prices significantly higher and raise recession risks for import‑dependent
            economies.
          </li>
          <li>
            <strong>Volatility and uncertainty</strong> often matter as much as the level of prices,
            affecting hedging costs, investment decisions, and fiscal planning.
          </li>
        </ul>
        <p className="text-xs text-slate-600 dark:text-on-surface-variant mt-3">
          For detailed, scenario‑based ranges and a full methodology disclaimer, see the dedicated{' '}
          <Link
            to="/strait-of-hormuz/economic-impact"
            className="text-primary-600 dark:text-primary underline"
          >
            Economic Impact
          </Link>{' '}
          page.
        </p>
      </section>

      {/* C. Country Dependency Snapshot & D. Timeline Preview */}
      <section className="mb-10 grid md:grid-cols-2 gap-6">
        <div className="rounded-stitch-xl border border-slate-200/80 bg-white dark:border-outline-variant/15 dark:bg-surface-container p-6">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-on-surface mb-3">
            Country Dependency Snapshot
          </h2>
          <p className="text-sm text-slate-700 dark:text-on-surface-variant mb-3">
            Some importers rely much more heavily than others on oil that transits Hormuz. This
            shapes both their economic vulnerability and their foreign‑policy priorities.
          </p>
          <div className="overflow-x-auto rounded-lg border bg-slate-100 dark:bg-surface-container-low mb-2">
            <table className="min-w-full text-xs">
              <thead>
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-slate-700 dark:text-on-surface-variant">
                    Country / Region
                  </th>
                  <th className="px-3 py-2 text-left font-semibold text-slate-700 dark:text-on-surface-variant">
                    Approx. dependency
                  </th>
                  <th className="px-3 py-2 text-left font-semibold text-slate-700 dark:text-on-surface-variant">
                    Risk level
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-outline-variant/20">
                <tr>
                  <td className="px-3 py-2 text-slate-900 dark:text-on-surface">Japan</td>
                  <td className="px-3 py-2 text-slate-700 dark:text-on-surface-variant">≈ 80–90% of crude imports</td>
                  <td className="px-3 py-2 text-red-600 dark:text-red-400 font-semibold">Very High</td>
                </tr>
                <tr>
                  <td className="px-3 py-2 text-slate-900 dark:text-on-surface">India</td>
                  <td className="px-3 py-2 text-slate-700 dark:text-on-surface-variant">often around ≈ 60%</td>
                  <td className="px-3 py-2 text-orange-600 dark:text-orange-400 font-semibold">High</td>
                </tr>
                <tr>
                  <td className="px-3 py-2 text-slate-900 dark:text-on-surface">China</td>
                  <td className="px-3 py-2 text-slate-700 dark:text-on-surface-variant">≈ 40–50%</td>
                  <td className="px-3 py-2 text-amber-600 dark:text-amber-400 font-semibold">
                    Medium–High
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-xs text-slate-600 dark:text-on-surface-variant">
            For a full map, charts, and narrative analysis, see the{' '}
            <Link
              to="/strait-of-hormuz/countries-dependent"
              className="text-primary-600 dark:text-primary underline"
            >
              Countries Dependent
            </Link>{' '}
            dashboard.
          </p>
        </div>

        <div className="rounded-stitch-xl border border-slate-200/80 bg-white dark:border-outline-variant/15 dark:bg-surface-container p-6">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-on-surface mb-3">
            Historical Timeline – How Crises Shape Today&apos;s Risk
          </h2>
          <p className="text-sm text-slate-700 dark:text-on-surface-variant mb-3">
            Incidents in Hormuz have repeatedly prompted changes in naval deployments, insurance
            pricing, and regulatory frameworks. A few key episodes illustrate the pattern.
          </p>
          <ul className="space-y-2 text-sm text-slate-700 dark:text-on-surface-variant list-disc list-inside mb-3">
            <li>
              <strong>Tanker War (1980s):</strong> Attacks on shipping highlighted the vulnerability
              of unescorted tankers and led to greater emphasis on convoys and naval presence.
            </li>
            <li>
              <strong>Late‑2010s tanker incidents:</strong> A series of attacks increased war‑risk
              premia and reminded markets that even &quot;peacetime&quot; routes can face sudden
              shocks.
            </li>
          </ul>
          <p className="text-xs text-slate-600 dark:text-on-surface-variant">
            The{' '}
            <Link
              to="/strait-of-hormuz/historical-timeline"
              className="text-primary-600 dark:text-primary underline"
            >
              Historical Timeline
            </Link>{' '}
            page provides a full list with &quot;what happened&quot;, &quot;why it mattered&quot;,
            and &quot;impact on trade&quot; for each event.
          </p>
        </div>
      </section>

      {/* E. Scenario Playbook Table */}
      <section className="mb-10 rounded-stitch-xl border border-slate-200/80 bg-white dark:border-outline-variant/15 dark:bg-surface-container p-6">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-on-surface mb-3">
          Scenario Playbook: From Harassment to Full Blockade
        </h2>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant mb-3">
          The same chokepoint can produce very different market outcomes depending on how severe,
          how long, and how clearly understood a disruption is. The table below summarises
          illustrative orders of magnitude for several stylised scenarios, using Brent crude as a
          reference point and focusing on the first two weeks of market adjustment.
        </p>
        <div className="overflow-x-auto rounded-lg border bg-slate-100 dark:bg-surface-container-low">
          <table className="min-w-full text-xs">
            <thead>
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-slate-700 dark:text-on-surface-variant">
                  Scenario
                </th>
                <th className="px-3 py-2 text-left font-semibold text-slate-700 dark:text-on-surface-variant">
                  Estimated Brent move (first 2 weeks)
                </th>
                <th className="px-3 py-2 text-left font-semibold text-slate-700 dark:text-on-surface-variant">
                  Physical disruption
                </th>
                <th className="px-3 py-2 text-left font-semibold text-slate-700 dark:text-on-surface-variant">
                  Logistics adjustment window
                </th>
                <th className="px-3 py-2 text-left font-semibold text-slate-700 dark:text-on-surface-variant">
                  Macro risk characterisation
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-outline-variant/20">
              <tr>
                <td className="px-3 py-2 text-slate-900 dark:text-on-surface">Brief harassment</td>
                <td className="px-3 py-2 text-slate-700 dark:text-on-surface-variant">≈ +5–10%</td>
                <td className="px-3 py-2 text-slate-700 dark:text-on-surface-variant">
                  Isolated incidents, no sustained loss of capacity
                </td>
                <td className="px-3 py-2 text-slate-700 dark:text-on-surface-variant">Days</td>
                <td className="px-3 py-2 text-slate-700 dark:text-on-surface-variant">
                  Short‑lived risk premium, limited macro impact outside sensitive importers
                </td>
              </tr>
              <tr>
                <td className="px-3 py-2 text-slate-900 dark:text-on-surface">
                  Partial disruption of flows
                </td>
                <td className="px-3 py-2 text-slate-700 dark:text-on-surface-variant">≈ +10–25%</td>
                <td className="px-3 py-2 text-slate-700 dark:text-on-surface-variant">
                  10–30% of volumes delayed or rerouted
                </td>
                <td className="px-3 py-2 text-slate-700 dark:text-on-surface-variant">
                  Several weeks as inventories and alternative routes absorb pressure
                </td>
                <td className="px-3 py-2 text-slate-700 dark:text-on-surface-variant">
                  Regional growth headwinds; higher inflation prints for fuel‑intensive economies
                </td>
              </tr>
              <tr>
                <td className="px-3 py-2 text-slate-900 dark:text-on-surface">Prolonged near‑closure</td>
                <td className="px-3 py-2 text-slate-700 dark:text-on-surface-variant">≈ +30–70%</td>
                <td className="px-3 py-2 text-slate-700 dark:text-on-surface-variant">
                  60–100% of seaborne flows constrained for weeks or more
                </td>
                <td className="px-3 py-2 text-slate-700 dark:text-on-surface-variant">
                  Months; requires structural reallocation of supply, demand, and trade routes
                </td>
                <td className="px-3 py-2 text-slate-700 dark:text-on-surface-variant">
                  Global slowdown risk; sharper stress for energy‑import‑dependent sovereigns
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="text-xs text-slate-600 dark:text-on-surface-variant mt-2">
          Ranges are illustrative bands based on patterns from past disruptions and assume no
          offsetting shock elsewhere. They are designed to support scenario planning, not precise
          forecasting.
        </p>
      </section>

      {/* Latest News Preview */}
      {latestNews.length > 0 && (
        <section className="mb-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-slate-900 dark:text-on-surface flex items-center">
              <Activity className="w-5 h-5 mr-2 text-primary-600" />
              Latest Strait of Hormuz Updates
            </h2>
            <Link
              to="/strait-of-hormuz/live-updates"
              className="text-sm text-primary-600 dark:text-primary hover:underline flex items-center"
            >
              View all updates
              <ArrowRight className="w-4 h-4 ml-1" />
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {latestNews.map(item => (
              <article
                key={item.id}
                className="rounded-stitch-xl border border-slate-200/80 bg-white p-4 dark:border-outline-variant/15 dark:bg-surface-container hover:shadow-sm transition-shadow"
              >
                <h3 className="text-sm font-semibold text-slate-900 dark:text-on-surface mb-2 line-clamp-2">
                  {item.headline}
                </h3>
                <p className="text-xs text-slate-500 dark:text-on-surface-variant mb-2">
                  {item.source} · {new Date(item.published_at).toLocaleDateString()}
                </p>
                <p className="text-sm text-slate-700 dark:text-on-surface-variant line-clamp-3 mb-3">
                  {item.ai_summary}
                </p>
                {item.url && (
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-xs text-primary-600 dark:text-primary hover:underline"
                  >
                    View source
                    <ExternalLink className="w-3 h-3 ml-1" />
                  </a>
                )}
              </article>
            ))}
          </div>
        </section>
      )}

      {/* TraderRadar Intelligence Insight */}
      <section className="mb-10 bg-slate-100 dark:bg-surface-container-low rounded-lg border border-slate-200 dark:border-outline-variant/15 p-6">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-on-surface mb-3">
          TraderRadar Intelligence Insight
        </h2>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant mb-3">
          What makes Hormuz unusual is not just the volume of oil that passes through it, but the
          way disruption risk is transmitted through multiple layers of the energy and financial
          system. Naval deployments, drone activity, or sanctions enforcement can change perceived
          risk within hours, long before any tanker is actually delayed. Markets therefore price
          Hormuz as an option on future disruption rather than a simple on/off switch for supply.
        </p>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant mb-3">
          For Gulf producers with partial overland or pipeline alternatives, the short‑term
          constraint is often not geology but contract structure and infrastructure bottlenecks. A
          producer that can reroute 10–20% of exports overland can soften the blow of a partial
          closure, but doing so may require renegotiating term contracts and reallocating shipping
          capacity. Importers that rely heavily on term cargoes indexed to regional benchmarks such
          as Dubai or Oman can find that their effective reference price diverges from global
          benchmarks when Hormuz risk is elevated.
        </p>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant mb-3">
          Second‑order effects show up in places that are easy to overlook. War‑risk premia and
          longer voyages tighten the effective supply of tankers, which can lift freight rates for
          routes that never touch the Gulf. Sovereign borrowing costs for highly import‑dependent
          economies can widen as investors anticipate larger current‑account deficits. For traders
          and risk managers, tracking these cross‑market linkages – freight futures, options skew,
          and credit spreads – often provides earlier warning of stress than watching spot prices
          alone.
        </p>
        <div className="mt-4 overflow-x-auto overflow-hidden rounded-stitch-xl border border-slate-200/80 bg-white dark:border-outline-variant/15 dark:bg-surface-container">
          <table className="min-w-full text-xs">
            <thead>
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-slate-700 dark:text-on-surface-variant">
                  Signal to monitor
                </th>
                <th className="px-3 py-2 text-left font-semibold text-slate-700 dark:text-on-surface-variant">
                  Why it matters
                </th>
                <th className="px-3 py-2 text-left font-semibold text-slate-700 dark:text-on-surface-variant">
                  Typical lead time vs. physical disruption
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-outline-variant/20">
              <tr>
                <td className="px-3 py-2 text-slate-900 dark:text-on-surface">
                  Gulf tanker war‑risk premia
                </td>
                <td className="px-3 py-2 text-slate-700 dark:text-on-surface-variant">
                  Insurance reprices before cargoes are cancelled, signalling how underwriters view
                  escalation odds.
                </td>
                <td className="px-3 py-2 text-slate-700 dark:text-on-surface-variant">
                  Often days to weeks ahead of measurable flow loss
                </td>
              </tr>
              <tr>
                <td className="px-3 py-2 text-slate-900 dark:text-on-surface">
                  VLCC and product tanker time‑charter rates
                </td>
                <td className="px-3 py-2 text-slate-700 dark:text-on-surface-variant">
                  Capture both routing detours and the willingness of owners to accept Gulf exposure.
                </td>
                <td className="px-3 py-2 text-slate-700 dark:text-on-surface-variant">
                  Days ahead of refinery run cuts and reported trade flow changes
                </td>
              </tr>
              <tr>
                <td className="px-3 py-2 text-slate-900 dark:text-on-surface">
                  Brent–Dubai and regional spreads
                </td>
                <td className="px-3 py-2 text-slate-700 dark:text-on-surface-variant">
                  Reflect how tight seaborne Gulf supplies are relative to alternative grades.
                </td>
                <td className="px-3 py-2 text-slate-700 dark:text-on-surface-variant">
                  Can move intraday as market participants rebalance exposure
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* H. Internal Linking Hub / Related Pages */}
      <section className="mb-12">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-on-surface mb-4">
          Explore the Strait of Hormuz Authority Hub
        </h2>
        <div className="grid md:grid-cols-3 gap-4">
          <HubLink
            to="/strait-of-hormuz/statistics"
            title="Key Statistics"
            description="Core metrics on flows, shares, and geometry of the chokepoint."
            icon={PieChart}
          />
          <HubLink
            to="/strait-of-hormuz/shipping-routes"
            title="Shipping Routes"
            description="Typical tanker paths, alternative routes, and added days and costs."
            icon={Map}
          />
          <HubLink
            to="/strait-of-hormuz/historical-timeline"
            title="Historical Timeline"
            description="How past crises reshaped risk perceptions and trade patterns."
            icon={Activity}
          />
          <HubLink
            to="/strait-of-hormuz/economic-impact"
            title="Economic Impact"
            description="Scenario-based price, trade, and macro ranges with clear caveats."
            icon={TrendingUp}
          />
          <HubLink
            to="/strait-of-hormuz/oil-impact-calculator"
            title="Oil Impact Calculator"
            description="Test your own disruption assumptions using a simple sensitivity model."
            icon={Droplet}
          />
          <HubLink
            to="/strait-of-hormuz/countries-dependent"
            title="Countries Dependent"
            description="Which economies are structurally most exposed to Hormuz flows."
            icon={Globe2}
          />
        </div>
      </section>

      {/* Related Risk Areas */}
      <section className="mb-12">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-on-surface mb-3">
          Related Risk Areas
        </h2>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant mb-4">
          Hormuz is part of a wider network of maritime chokepoints that shape global trade
          resilience. For a complete view of route substitution and systemic risk, it is useful to
          consider how disruptions here might interact with constraints elsewhere.
        </p>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="rounded-stitch-xl border border-slate-200/80 bg-white p-4 dark:border-outline-variant/15 dark:bg-surface-container">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-on-surface mb-1">
              Suez Canal &amp; Red Sea Corridor
            </h3>
            <p className="text-xs text-slate-700 dark:text-on-surface-variant mb-2">
              Links the Gulf and Indian Ocean to Europe. Simultaneous stress in Hormuz and Suez
              would force far longer diversions via the Cape of Good Hope.
            </p>
          </div>
          <div className="rounded-stitch-xl border border-slate-200/80 bg-white p-4 dark:border-outline-variant/15 dark:bg-surface-container">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-on-surface mb-1">
              Strait of Malacca
            </h3>
            <p className="text-xs text-slate-700 dark:text-on-surface-variant mb-2">
              Primary route for Asia‑bound crude and container traffic. Hormuz disruptions amplify
              Malacca congestion risks for refiners in Northeast and Southeast Asia.
            </p>
          </div>
          <div className="rounded-stitch-xl border border-slate-200/80 bg-white p-4 dark:border-outline-variant/15 dark:bg-surface-container">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-on-surface mb-1">
              Panama Canal &amp; Atlantic Alternatives
            </h3>
            <p className="text-xs text-slate-700 dark:text-on-surface-variant mb-2">
              While geographically distant, capacity constraints in Panama can limit Atlantic basin
              flexibility just when importers are seeking alternatives to Gulf barrels.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      {faqs.length > 0 && (
        <section className="mb-12">
          <h2 className="text-xl font-semibold text-slate-900 dark:text-on-surface mb-4">
            Strait of Hormuz – Frequently Asked Questions
          </h2>
          <div className="divide-y divide-slate-200 dark:divide-outline-variant/20">
            {faqs.map((faq, idx) => (
              <details key={faq.id || idx} className="py-4 group">
                <summary className="cursor-pointer flex justify-between items-center text-base font-medium text-slate-900 dark:text-on-surface hover:text-primary-600 dark:hover:text-primary-400">
                  <span>{faq.title}</span>
                  <span className="ml-2 text-xs text-primary-600 group-open:hidden">Show</span>
                  <span className="ml-2 text-xs text-primary-600 hidden group-open:inline">Hide</span>
                </summary>
                <div className="mt-3 text-sm text-slate-700 dark:text-on-surface-variant pl-4">
                  {faq.answer || faq.content}
                </div>
              </details>
            ))}
          </div>
        </section>
      )}

      {/* Article Content (if available) */}
      {article?.content && (
        <section className="mb-10">
          <div
            className="prose dark:prose-invert max-w-none"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />
        </section>
      )}
      </div>
    </article>
  );
};

// Stat Card Component
const StatCard = ({ label, value, icon: Icon }) => (
  <div className="rounded-stitch-xl border border-slate-200/80 bg-white p-4 dark:border-outline-variant/15 dark:bg-surface-container flex items-center space-x-3">
    {Icon && <Icon className="w-6 h-6 text-primary-600 flex-shrink-0" />}
    <div>
      <p className="text-xs text-slate-500 dark:text-on-surface-variant">{label}</p>
      <p className="text-lg font-semibold text-slate-900 dark:text-on-surface">{value}</p>
    </div>
  </div>
);

// Hub Link Component
const HubLink = ({ to, title, description, icon: Icon }) => (
  <Link
    to={to}
    className="rounded-stitch-xl border border-slate-200/80 bg-white p-5 dark:border-outline-variant/15 dark:bg-surface-container hover:shadow-md transition-shadow flex flex-col group"
  >
    {Icon && (
      <div className="mb-3 p-2 rounded-lg bg-primary-50 dark:bg-primary-900/20 w-fit">
        <Icon className="w-5 h-5 text-primary-600 dark:text-primary" />
      </div>
    )}
    <h3 className="text-base font-semibold text-slate-900 dark:text-on-surface mb-2 group-hover:text-primary-600 dark:group-hover:text-primary-400">
      {title}
    </h3>
    <p className="text-sm text-slate-600 dark:text-on-surface-variant flex-1 mb-3">{description}</p>
    <span className="text-xs text-primary-600 dark:text-primary inline-flex items-center group-hover:underline">
      Learn more
      <ArrowRight className="w-3 h-3 ml-1" />
    </span>
  </Link>
);

export default HormuzOverview;

